/**
 * Загрузка ручных выгрузок Excel.
 *
 * Нужна, пока не готова интеграция с 1С: даёт реальные цифры по номенклатуре
 * из отчёта «Валовая прибыль предприятия».
 *
 * Файл принимается только если сумма загруженных строк сошлась с итоговой
 * строкой самого файла. Расхождение означает, что мы прочитали не то —
 * пропустили строки, не туда попали колонки — и грузить такое нельзя.
 *
 * С указанным месяцем файл ещё и сверяется с выгрузкой из 1С за тот же
 * месяц (пункт 7 приёмки): сошлось или нет, в каком варианте и на каких
 * товарах разошлось. Результат виден и в кабинете подрядчика.
 */

import { Icon } from '../components/Icon'
import { useEffect, useRef, useState, type DragEvent } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '../api'
import { Card, Field, Notice } from '../components/ui'
import { formatMoney, pluralize } from '../format'
import { GrossProfitCheckView } from '../components/GrossProfitCheck'

type Result = Awaited<ReturnType<typeof api.uploadGrossProfit>>

const METRIC_TITLES: Record<string, string> = {
  revenue: 'Выручка',
  cost_total: 'Себестоимость',
  cost_purchase: 'Стоимость закупки',
  cost_extra: 'Дополнительные расходы',
  gross_profit: 'Валовая прибыль',
  avg_stock_value: 'Средняя стоимость запасов',
}

export function Upload() {
  const inputRef = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const [periodLabel, setPeriodLabel] = useState('')
  const [month, setMonth] = useState('')
  const [databases, setDatabases] = useState<string[]>([])
  const [database, setDatabase] = useState('')
  const [result, setResult] = useState<Result | null>(null)
  const [error, setError] = useState<ApiError | null>(null)

  // Базы 1С, из которых что-то приходило: сверять есть с чем только с ними.
  useEffect(() => {
    let cancelled = false
    api
      .ingestProgress()
      .then((progress) => {
        if (cancelled) return
        setDatabases(progress.databases ?? [progress.database_id])
        setDatabase((current) => current || progress.database_id)
      })
      .catch(() => {
        // Без списка баз сервер возьмёт ту, откуда последними пришли продажи.
      })
    return () => {
      cancelled = true
    }
  }, [])

  const upload = async (file: File) => {
    setBusy(true)
    setError(null)
    setResult(null)

    try {
      setResult(
        await api.uploadGrossProfit(file, {
          periodLabel: periodLabel || undefined,
          month: month || undefined,
          sourceDatabaseId: month && database ? database : undefined,
        }),
      )
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Не удалось загрузить файл', 500))
    } finally {
      setBusy(false)
    }
  }

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setOver(false)
    const file = event.dataTransfer.files[0]
    if (file) void upload(file)
  }

  return (
    <div className="stack" style={{ gap: 18 }}>
      <Notice tone="info">
        Данные из файла не смешиваются с данными из 1С: в выгрузке нет ни периода, ни филиала,
        ни контрагента, а ключ строки — текстовое наименование. Они попадают в отдельный отчёт
        с явной пометкой источника.
      </Notice>

      <Card title="Отчёт «Валовая прибыль предприятия»">
        <div className="stack" style={{ gap: 16 }}>
          <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
            <Field
              label="Месяц для сверки с 1С"
              hint="Если указать, итоги файла сравнятся с выгрузкой за этот месяц. Без месяца файл просто загрузится"
            >
              <MonthPicker value={month} onChange={setMonth} />
            </Field>
            {month && databases.length > 0 && (
              <Field label="С какой базой 1С сравнивать">
                <select className="select" value={database} onChange={(event) => setDatabase(event.target.value)}>
                  {databases.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>

          <Field
            label="Метка периода"
            hint="Если не указать: месяц сверки, иначе год из имени файла («…_2024_г_2.xlsx» → 2024). Файл с той же меткой заменяет прежний"
          >
            <input
              className="input"
              placeholder="2024"
              value={periodLabel}
              onChange={(event) => setPeriodLabel(event.target.value)}
              style={{ maxWidth: 200 }}
            />
          </Field>

          <div
            className={`dropzone${over ? ' dropzone--over' : ''}`}
            onDragOver={(event) => {
              event.preventDefault()
              setOver(true)
            }}
            onDragLeave={() => setOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
          >
            <div className="dropzone__icon" aria-hidden="true">
              <Icon name="upload" size={22} />
            </div>
            <div className="dropzone__title">
              {busy ? 'Разбираем файл…' : 'Перетащите файл .xlsx или нажмите, чтобы выбрать'}
            </div>
            <div className="dropzone__hint">
              Шапка ищется по тексту, а не по номеру строки: перестановка колонок не сломает загрузку
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) void upload(file)
                event.target.value = ''
              }}
            />
          </div>
        </div>
      </Card>

      {error && (
        <Notice tone="bad">
          <strong>Файл не загружен. </strong>
          {error.message}
          {Boolean(error.details) && (
            <div className="tiny" style={{ marginTop: 6 }}>
              <code>{JSON.stringify(error.details)}</code>
            </div>
          )}
        </Notice>
      )}

      {result && (
        <Card title="Файл загружен">
          <div className="stack" style={{ gap: 14 }}>
            <div className="row">
              <span className="strong">{result.file_name}</span>
              <span className="badge badge--good">
                {pluralize(result.rows_loaded, 'строка', 'строки', 'строк')}
              </span>
              {result.lines_without_cost > 0 && (
                <span className="badge badge--warn">
                  {pluralize(result.lines_without_cost, 'строка', 'строки', 'строк')} без себестоимости
                </span>
              )}
              {result.total_row_found && <span className="badge">строка «Итого» распознана</span>}
            </div>

            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Показатель</th>
                    <th className="num">Итог в файле</th>
                    <th className="num">Сумма загруженных строк</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(result.totals.declared)
                    .filter(([, value]) => value !== null)
                    .map(([key, declared]) => (
                      <tr key={key}>
                        <td>{METRIC_TITLES[key] ?? key}</td>
                        <td className="num">{formatMoney(declared, true)}</td>
                        <td className="num">{formatMoney(result.totals.computed[key], true)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="tiny muted">
              Файл принимается только при полном совпадении с итоговой строкой: расхождение означало бы,
              что часть строк не прочиталась.
            </div>

            {result.reconciliation ? (
              <>
                <h4 className="panel__title section-title">Сверка с выгрузкой из 1С</h4>
                <GrossProfitCheckView check={result.reconciliation} />
              </>
            ) : (
              <div className="tiny muted">
                С выгрузкой из 1С не сверялось: месяц не указан. Чтобы сверить, загрузите файл ещё раз с месяцем —
                файл с той же меткой периода заменит этот.
              </div>
            )}

            <div>
              <Link to="/reports/gross_profit_manual" className="btn btn--primary">
                Открыть отчёт по этим данным <Icon name="arrow-right" size={14} />
              </Link>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}

const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

/**
 * Месяц и год двумя списками вместо <input type="month">: в русской
 * локали Chrome показывает в нём «--------- ----», и непонятно, что вводить.
 * Пустой месяц — «не сверять».
 */
function MonthPicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const now = new Date()
  const currentYear = now.getFullYear()
  const [year, setYear] = useState(() => (value ? Number(value.slice(0, 4)) : currentYear))
  const monthIndex = value ? Number(value.slice(5, 7)) - 1 : -1
  const years = Array.from({ length: 5 }, (_, index) => currentYear - index)
  const emit = (nextYear: number, nextMonth: number) =>
    onChange(nextMonth < 0 ? '' : `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}`)
  return (
    <div className="row row--tight month-picker">
      <select className="select" value={monthIndex} onChange={(event) => emit(year, Number(event.target.value))} aria-label="Месяц">
        <option value={-1}>Не сверять</option>
        {MONTHS.map((name, index) => (
          <option key={name} value={index} disabled={year === currentYear && index > now.getMonth()}>
            {name}
          </option>
        ))}
      </select>
      <select
        className="select"
        value={year}
        onChange={(event) => {
          const next = Number(event.target.value)
          setYear(next)
          if (monthIndex >= 0) emit(next, next === currentYear ? Math.min(monthIndex, now.getMonth()) : monthIndex)
        }}
        aria-label="Год"
      >
        {years.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
    </div>
  )
}
